import { auth } from "../firebase-config";

export const getTaskExplanation = async (taskTitle) => {
    try {
        const response = await fetch(process.env.REACT_APP_TASK_EXPLANATION, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ taskDescription: taskTitle }),
        });
        const data = await response.json();
        return data.explanation;
    } catch (error) {
        return 'Failed to get task explanation. Please try again later.';
    }
};

export const getEmailReply = async (emailContent) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
        const response = await fetch(process.env.REACT_APP_EMAIL_REPLY, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ emailBody: emailContent }),
            signal: controller.signal,
        });

        clearTimeout(timeoutId);
        if (!response.ok) {
            throw new Error(`Server error: ${response.status}`);
        }
        const data = await response.json();
        return data.explanation;
    } catch (error) {
        return 'Failed to get email reply. Please try again later.';
    }
};

export const getEmailSummary = async (emailContent) => {
    try {
        const response = await fetch(process.env.REACT_APP_EMAIL_SUMMARY, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ emailSummary: emailContent }),
        });

        const data = await response.json();
        return data.explanation;
    } catch (error) {
        return 'Failed to get task explanation. Please try again later.';
    }
};

export const geminiChat = async (userInput, setUserInput, chatHistory, setChatHistory, setIsLoading) => {
    if (!userInput.trim()) return;
    setChatHistory(prev => [...prev, { role: "user", content: userInput }]);
    setIsLoading(true);
    try {
        const response = await fetch(process.env.REACT_APP_GEMINI_CHAT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                message: userInput,
                history: chatHistory,
            })
        });
        const data = await response.json();
        if (response.ok) {
            setChatHistory(prev => [...prev, { role: "assistant", content: data.response }]);
        } else {
            console.error("API error:", data);
            setChatHistory(prev => [...prev, {
                role: "assistant",
                content: `Error: ${data.message || "Something went wrong. Please try again."}`,
            }]);
        }
    } catch (error) {
        console.error("Network error:", error);
        setChatHistory(prev => [...prev, {
            role: "assistant",
            content: "Network error. Please check your connection and try again.",
        }]);
    } finally {
        setUserInput("");
        setIsLoading(false);
    }
};

export const createRazorpayOrder = async (amount, tierName, billingCycle, userId) => {
    try {
        const response = await fetch(process.env.REACT_APP_CREATE_RAZORPAY_ORDER, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ amount, tierName, billingCycle, userId }),
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to create order');
        }
        return await response.json();
    } catch (error) {
        console.error("Error creating Razorpay order:", error);
        throw error;
    }
};

export const initiateRazorpayPayment = async (amount, tierName, billingCycle, userId) => {
    try {
        //const userDocRef = doc(firestore, 'users', userId);
        //const userDoc = await getDoc(userDocRef);
        //if (!userDoc.exists()) {
        //    console.log("Creating new user document for:", userId);
        //    const currentUser = auth.currentUser;
        //    if (!currentUser) {
        //        throw new Error("User not authenticated");
        //    }
        //    await setDoc(userDocRef, {
        //        email: currentUser.email,
        //        displayName: currentUser.displayName || "",
        //        photoURL: currentUser.photoURL || "",
        //        createdAt: serverTimestamp(),
        //        isStarter: false,
        //        isPro: false,
        //        isEnterprise: false,
        //        subscriptionTier: 0,
        //        lastUpdated: serverTimestamp(),
        //    });
        //}
        const orderData = await createRazorpayOrder(amount, tierName, billingCycle, userId);
        return new Promise((resolve, reject) => {
            const options = {
                key: process.env.REACT_APP_RAZORPAY_TEST_KEY_ID,
                amount: orderData.amount,
                currency: orderData.currency,
                name: "Corella",
                description: `${tierName} Subscription - ${billingCycle}`,
                order_id: orderData.id,
                handler: async function (response) {
                    try {
                        const result = await verifyRazorpayPayment(response, userId);
                        if (result.success) {
                            resolve(result);
                        } else {
                            await cancelRazorpayOrder(orderData.id);
                            reject(new Error("Payment verification failed"));
                        }
                    } catch (error) {
                        await cancelRazorpayOrder(orderData.id);
                        reject(error);
                    }
                },
                modal: {
                    ondismiss: async function () {
                        await cancelRazorpayOrder(orderData.id);
                        reject(new Error("Payment cancelled"));
                    }
                },
                prefill: {
                    name: auth.currentUser?.displayName || "",
                    email: auth.currentUser?.email || "",
                    contact: "",
                },
                theme: { color: "#546FFF" }
            };
            const paymentObject = new window.Razorpay(options);
            paymentObject.on('payment.failed', async function (response) {
                await cancelRazorpayOrder(orderData.id);
                reject(new Error("Payment failed"));
            });
            paymentObject.open();
        });
    } catch (error) {
        console.error("Payment initialization error:", error);
        throw error;
    }
};

export const verifyRazorpayPayment = async (paymentData, userId) => {
    try {
        const response = await fetch(process.env.REACT_APP_VERIFY_RAZORPAY_PAYMENT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...paymentData, userId }),
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Payment verification failed');
        }
        return await response.json();
    } catch (error) {
        console.error("Error verifying payment:", error);
        throw error;
    }
};

export const cancelRazorpayOrder = async (orderId) => {
    try {
        const response = await fetch(process.env.REACT_APP_CANCEL_RAZORPAY_ORDER, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ orderId }),
        });

        if (!response.ok) {
            console.error('Failed to cancel order');
            return false;
        }

        return await response.json();
    } catch (error) {
        console.error('Error cancelling Razorpay order:', error);
        return false;
    }
};